import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FiHome, FiFolder, FiFile, FiMessageSquare, FiSearch, FiCheckSquare, FiList, FiUsers, FiLogOut, FiSettings, FiShield, FiMenu, FiX, FiSmartphone } from 'react-icons/fi';
import useAuthStore from '../store/authStore';
import MobileAuthModal from './MobileAuthModal';
import { adminAPI } from '../lib/api';

const Layout = ({ children }) => {
  const location = useLocation();
  const { profile, logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileAuthOpen, setIsMobileAuthOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  // Load company logo
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await adminAPI.getCompanyLogo();
        if (response.data?.logo_url) {
          setLogoUrl(response.data.logo_url);
        }
      } catch (error) {
        console.error('Failed to load logo:', error);
      }
    };
    loadLogo();
  }, []);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: FiHome, roles: null },
    { name: 'Documents', path: '/documents', icon: FiFile, roles: null },
    { name: 'Folders', path: '/folders', icon: FiFolder, roles: null },
    { name: 'AI Assistant', path: '/chatbot', icon: FiMessageSquare, roles: null },
    { name: 'Search', path: '/search', icon: FiSearch, roles: null },
    { name: 'Me To Action', path: '/me-to-action', icon: FiCheckSquare, roles: null },
    { name: 'Audit Log', path: '/audit-log', icon: FiList, roles: ['admin'] },
    { name: 'Users', path: '/users', icon: FiUsers, roles: ['admin'] },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', path: '/admin/dashboard', icon: FiHome, roles: ['admin'] },
    { name: 'Security Events', path: '/admin/security', icon: FiShield, roles: ['admin'] },
    { name: 'System Settings', path: '/admin/settings', icon: FiSettings, roles: ['admin'] },
  ];

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const hasAccess = (roles) => {
    if (!roles) return true;
    return roles.includes(profile?.role);
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg text-gray-700 hover:bg-gray-100"
      >
        {isMobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`w-64 bg-white border-r border-gray-200 flex flex-col fixed h-screen z-40 transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Logo */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-center">
            <img
              src={logoUrl || "/images/CIM-logo-(600-x-242).png"}
              alt="Company Logo"
              className="h-12 w-auto"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            if (!hasAccess(item.roles)) return null;

            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}

          {/* Admin Section */}
          {profile?.role === 'admin' && (
            <>
              <div className="pt-4 pb-2 px-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Administration
                </h3>
              </div>
              {adminNavigation.map((item) => {
                if (!hasAccess(item.roles)) return null;

                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                      active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          {/* Mobile Auth Button */}
          <button
            onClick={() => {
              setIsMobileAuthOpen(true);
              setIsMobileMenuOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors mb-3"
          >
            <FiSmartphone className="w-5 h-5" />
            <span className="font-medium">Link Mobile App</span>
          </button>

          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-medium text-gray-900">{profile?.full_name}</p>
              <p className="text-sm text-gray-500">{profile?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <FiLogOut className="w-5 h-5" />
            </button>
          </div>

          {/* Version Info */}
          <div className="text-center pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">V-Y2025-Q4-REF01</p>
          </div>
        </div>
      </div>

      {/* Mobile Auth Modal */}
      <MobileAuthModal
        isOpen={isMobileAuthOpen}
        onClose={() => setIsMobileAuthOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto lg:ml-64">
        <div className="max-w-7xl mx-auto p-4 lg:p-8 mt-16 lg:mt-0">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
